import type { TriageBookAs, TriageBookLike } from "./TriageBookLike";
import type { TriageJournalSettlementContext } from "./TriageJournalEntryPanel";
import { createContext, useContext } from "react";

import type {
  DocumentFragment,
  SettlementTreatmentEnum,
  TransactionFragment,
  TriageDetailQuery,
  TransactionFilter,
} from "@/__generated__/graphql";
import { identifierAndTypenameToId } from "@/objects/@utils";

import type { TriageOptionListItem } from "./TriageSuggestionsPanel";
import type { TriageResolutionTab } from "./TriageView.resolution";

/** The detail panel's node: base Triage fields plus its suggestion stubs (``TRIAGE_DETAIL_QUERY``). */
export type TriageDetail = Extract<
  NonNullable<TriageDetailQuery["node"]>,
  { __typename?: "TransactionObject" }
>;

type TriageSuggestionFromFragment = NonNullable<
  TriageDetail["suggestions"]
>[number];

/** Lean suggestion stub from the detail query. */
export type TriageSuggestionStub = TriageSuggestionFromFragment;

/**
 * One kind of suggestion's button in the toolbar - Match, Duplicates,
 * Transfer, Post - and what it opens to: on each, its suggestions worked out
 * again and the list of all it searches to pick from by hand; on Match first,
 * each mailbox to search, one more to connect and the address to forward to.
 */
export type TriageGroupMenu = {
  group: NonNullable<TriageOptionListItem["group"]>;
  actions: TriageOptionListItem[];
  /** At work: its suggestions worked out again - or, Match, a mailbox searched. */
  isBusy: boolean;
  /** The keyboard on a card of its kind: O opens it, and its key is said. */
  isFocused: boolean;
  /** ⌘↵ thinks on its kind - the keyboard on one of its cards, nothing picked to book - and its Think says so. */
  isThinkingByKey: boolean;
};

/** A category picked for the payment: one of its account's own, chosen on the account's row. */
type TriageCategoryOption = {
  id: string;
  identifier: string;
  category: NonNullable<
    TriageChartedAccountCatalogItem["categories"]
  >[number] & {
    /** The account it narrows. */
    parent: { id: string; identifier: string; name?: string | null };
  };
};
type TriageDocumentOption = NonNullable<
  NonNullable<
    TriageSuggestionFromFragment["ruleSuggestionDocumentOptions"]
  >[number]
>;
type TriageTagOption = NonNullable<
  NonNullable<TriageSuggestionFromFragment["ruleSuggestionTagOptions"]>[number]
>;
type TriageChartedAccountOption =
  TriageSuggestionFromFragment["ruleSuggestionChartedAccountOption"];
type TriageDuplicateOption = NonNullable<
  NonNullable<
    TriageSuggestionFromFragment["ruleSuggestionTransactionOptions"]
  >[number]
>;

/** What the payment's Similar says: see ``TriageDetailContextValue.bookLikeOffer``. */
export type TriageBookLikeOffer = {
  count: number | null;
  isLoading: boolean;
  isDisabled: boolean;
  /** The list itself, ticked as it was left: what its Move moves with it. */
  bookLike: TriageBookLike;
  /** What the payment open is about to be booked as: what the ticked are booked like. */
  bookAs: TriageBookAs | null;
  /** Whether its own Book books it as it is picked. */
  canBookItself: boolean;
  /** The payment open booked by its own Book. */
  onBook: () => void;
};

/** One of the bulk list's matches: each ticked payment's transfer, the payment it copies, or its own document. */
export type TriageBulkMatch = "transfers" | "duplicates" | "documents";

/** Where a match's line-up stands, for the toolbar: its lock, and its email search. */
export type TriageLineUpStatus = {
  /** The two sides' keys move together. */
  isLocked: boolean;
  toggleLock: () => void;
  /** The mailboxes searched for the payment the keys are on - none where they are on no payment, or nothing is matched to documents. */
  searchEmail: (() => void) | null;
  /** Its search under way. */
  isSearchingEmail: boolean;
};

/** The business of what a payment is lined up with. */
export type TriageLinedBusiness = {
  id?: string | null;
  identifier?: string | null;
  name?: string | null;
};

/**
 * Book, in the suggestions toolbar, while Similar is on: what the bulk list's
 * row picked does to the ticked payments - its words, whether it can be
 * pressed, whether it is at it - and doing it.
 */
export type TriageBulkBook = {
  label: string;
  isDisabled: boolean;
  isMaking: boolean;
  run: () => void;
};

export type TriageChartedAccountCatalogItem = {
  id: string;
  identifier: string;
  name?: string | null;
  path?: string | null;
  fullPath?: string[] | null;
  description?: string | null;
  categories?: Array<{
    id: string;
    identifier: string;
    name?: string | null;
    color?: string | null;
  }> | null;
};

export type TriageResolvedAdvanceOptions = {
  /**
   * Extra bank-transaction UUIDs that join the accepted journal entry (peer
   * payments / multi-leg changelist links). Matching triage list rows are
   * removed from the connection along with the accepted item.
   */
  alsoRemoveTransactionIdentifiers?: string[];
};

export type TriageViewActions = {
  /** Pick a payment and open the documents to look through: what its "No document" button does. */
  findDocumentFor?: (triageId: string) => void;
  /** The open payment's payee's others in the queue's place: its row's Similar, as L does. */
  openSimilar?: () => void;
  /** Accept & next: drop the row from the list cache and select the neighbor. */
  onResolved: (options?: TriageResolvedAdvanceOptions) => void;
  /**
   * Hold a payment, at once: its row says so as it is asked for, the
   * server's answer puts its own word in its place, and a refusal takes it
   * back. The payment stays selected.
   */
  holdTriage: (triageId: string) => void;
  /** Take a payment off hold, at once, as ``holdTriage`` puts one on: what its "On hold" chip does. */
  takeOffHold?: (triageId: string) => void;
  /** Publish staged row edits to the active list row without wrapping the list in detail context. */
  registerActiveRowStaging: (staging: TriageActiveRowStaging | null) => void;
  registerAccept: (handler: (() => void) | null) => void;
  /** Fire the registered Accept & next handler from list or suggestions hotkeys. */
  triggerAccept: () => void;
  /** Enter suggestions from the triage list (ArrowRight / Enter). */
  registerEnterSuggestions: (handler: (() => void) | null) => void;
  /** Leave list focus and move to the first suggestion for ``triageId``. */
  enterSuggestions: (triageId: string) => void;
  /** Select ``triageId`` (if needed) and open the thread panel. */
  openChat: (triageId: string) => void;
  /** The open payment's row offers its tags to change: what X opens, from the queue or the suggestions. */
  registerOpenTags: (handler: (() => void) | null) => void;
  /** Open the open payment's tags on its row - X. */
  openTags: () => void;
  /** Clear the selected triage and return keyboard focus to the list. */
  deselectTriage: () => void;
  /** Focus the triage list scope (e.g. Escape from inside a row). */
  focusTriageList: () => void;
  /** Take payments booked with the one booked, by identifier, out of the queue (``TriageBookLikeOffer``). */
  removeFromQueue: (identifiers: string[]) => void;
  /**
   * Take payments out of the queue while they are being booked - kept out
   * though the queue is read again before the server has booked them - until
   * the release it hands back is called: then those booked are gone, and
   * any refused come back as the queue is read again.
   */
  holdOffQueue: (identifiers: string[]) => () => void;
};

export const TriageViewActionsContext = createContext<TriageViewActions>({
  onResolved: () => {},
  holdTriage: () => {},
  registerActiveRowStaging: () => {},
  registerAccept: () => {},
  triggerAccept: () => {},
  registerEnterSuggestions: () => {},
  enterSuggestions: () => {},
  openChat: () => {},
  registerOpenTags: () => {},
  openTags: () => {},
  deselectTriage: () => {},
  focusTriageList: () => {},
  removeFromQueue: () => {},
  holdOffQueue: () => () => {},
});

export function useTriageViewActions() {
  return useContext(TriageViewActionsContext);
}

/**
 * Whether the queue's row a name is drawn on is the payment picked - set by
 * the queue alone, on each of its rows. On one that is not, a click on its
 * business, its bank account, or the business its half is for picks the
 * payment, as a click anywhere on it does - they cover so much of a card
 * that, opening what they name instead, a click seemed to go nowhere.
 * Outside the queue's rows, a name opens what it names.
 */
export const TriageRowPickedContext = createContext(true);

export type TriageSelectedTag = {
  __typename: "TagObject";
  id: string;
  identifier: string;
  name: string;
  color?: string | null;
};

export type TriageSuggestionSelections = {
  categoryIdentifier: string | null;
  chartedAccountIdentifier: string | null;
  documentIdentifiers: string[];
  /** Relay node ids captured at selection time — avoids catalog/cache races. */
  documentNodeIdsByIdentifier: Record<string, string>;
  /** Transactions selected on the Match → Transactions tab (journal multiselect). */
  transactionIdentifiers: string[];
  tagOptionIds: string[];
  /** Payer-entity tags staged until Accept. */
  selectedTags: TriageSelectedTag[];
  /** Settlement-entity tags staged until Accept (when on-behalf-of is set). */
  settlementTags: TriageSelectedTag[];
  chartedAccountOptionId: string | null;
  duplicateOptionId: string | null;
  /** Another business picked by hand for a payment coded by hand (staged until Accept). */
  settlementEntityIdentifier: string | null;
  /** Active balanced MatchSuggestion changelist on the Suggestions tab. */
  linesSuggestionIdentifier: string | null;
  /**
   * The category the person changed the picked suggestion to - one of the
   * account it posts to - booked in place of the one it suggests. Let go of
   * as another suggestion is picked.
   */
  suggestionCategoryIdentifier: string | null;
  /**
   * A category the person named on the picked suggestion's card, one the
   * account it posts to has none of: kept here, and made under that account as
   * it is booked. Let go of as another suggestion is picked, or a category
   * chosen.
   */
  suggestionNewCategoryName: string | null;
  /**
   * The same, named on the account picked by hand - one found for what was
   * typed: made under it as it is booked. Let go of as another is picked, or a
   * category chosen.
   */
  newCategoryName: string | null;
  /**
   * A draft still in review in a chat, picked on the Suggestions tab: booked,
   * it is confirmed and booked at once. Picking anything else lets it go.
   */
  draftDocumentIdentifier: string | null;
  /**
   * How a payment coded by hand for another business is settled between the
   * two - asked as it is accepted.
   */
  settlementTreatment: SettlementTreatmentEnum | null;
  /**
   * Whether a document match keeps a match its document has with another
   * payment, by the suggestion - or books this one in its place: the answer
   * given on its card. Kept where none is.
   */
  matchesKeptBySuggestion: Record<string, boolean>;
  /**
   * The other payments booked with a document match, by the suggestion: those
   * added on its card to pay the rest of its document. None until one is.
   */
  partsBySuggestion: Record<string, string[]>;
};

export type TriageOptionCatalog = {
  chartedAccountsByIdentifier: Record<string, TriageChartedAccountCatalogItem>;
  documentsByIdentifier: Record<string, DocumentFragment>;
  transactionsByIdentifier: Record<string, TransactionFragment>;
};

export type TriageResolvedOptions = {
  categoryOption: TriageCategoryOption | null;
  documentOption: TriageDocumentOption | null;
  documentOptions: TriageDocumentOption[];
  tagOption: TriageTagOption | null;
  tagOptions: TriageTagOption[];
  chartedAccountOption: TriageChartedAccountOption | null;
  duplicateOption: TriageDuplicateOption | null;
  categorizeSuggestion: TriageSuggestionStub | null;
  documentSuggestion: TriageSuggestionStub | null;
  duplicateSuggestion: TriageSuggestionStub | null;
  linesSuggestion: TriageSuggestionStub | null;
  selectedTransactions: TransactionFragment[];
};

export type TriageActivePreview = {
  kind: "account" | "category" | "tag" | "document" | "duplicate" | null;
  categoryOption: TriageResolvedOptions["categoryOption"];
  tagOption: TriageResolvedOptions["tagOption"];
  documentOption: TriageResolvedOptions["documentOption"];
  chartedAccountOption: TriageResolvedOptions["chartedAccountOption"];
  duplicateOption: TriageResolvedOptions["duplicateOption"];
  categorizeSuggestion: TriageSuggestionStub | null;
  documentSuggestion: TriageSuggestionStub | null;
  duplicateSuggestion: TriageSuggestionStub | null;
};

export type TriageDetailContextValue = {
  triage: TriageDetail;
  selections: TriageSuggestionSelections;
  setSelection: <K extends keyof TriageSuggestionSelections>(
    key: K,
    value:
      | TriageSuggestionSelections[K]
      | ((
          current: TriageSuggestionSelections[K],
        ) => TriageSuggestionSelections[K]),
  ) => void;
  activeOptionKey: string | null;
  setActiveOptionKey: (key: string | null) => void;
  activePreview: TriageActivePreview;
  resolved: TriageResolvedOptions;
  checkedCategoryOptionIds: Record<string, boolean>;
  toggleCategoryOptionChecked: (optionId: string) => void;
  /** True after Enter from the triage list; gates highlight, keyboard, and commits. */
  suggestionsEngaged: boolean;
  /** Put the keyboard in the suggestions - as a click on a card there does. */
  engageSuggestions: () => void;
  isSuggestionsKeyboardActive: () => boolean;
  /** True while triage detail query is in flight or not yet ready. */
  isDetailLoading: boolean;
  /** True while the detail query is in flight for the current list row. */
  detailQueryLoading: boolean;
  /**
   * Search the mailbox picked for the payment's document, there and then -
   * no AI - what is found offered on a card of its own, and a toast when
   * nothing is. With none picked and words of the person's own, every mailbox
   * a search looks in, for those words.
   */
  searchMailbox: (connectionIdentifier: string | null, query?: string) => void;
  /**
   * Say the payment is another business's - settled between the two as
   * *treatment* says - or, its own named, its own again: every one of its
   * suggestions worked out again settled there, and read once they are.
   */
  movePayment: (
    entityIdentifier: string,
    treatment: SettlementTreatmentEnum | null,
    /** The payments its Similar lists, ticked: moved with it, in the same request. */
    others?: string[],
  ) => void;
  /** Turn one suggestion down: it is no longer offered, and - picked - no longer picked. */
  rejectSuggestion: (suggestionIdentifier: string) => void;
  /** The business the payment is being moved to, until its suggestions there are read. */
  movingPaymentTo: string | null;
  /**
   * Work a card's suggestions out again by its rules alone, by its kinds -
   * the same every time, no AI asked. Each card on its own: one asked for
   * while another is, is worked out beside it.
   */
  rerunSuggestions: (group: { id: string; jobs: readonly string[] }) => void;
  /** The cards whose suggestions are being worked out again, by id, till what they come to is read. */
  rerunningSuggestions: string[];
  /** True while the AI is working out suggestions for the payment, whoever asked. */
  isProcessing: boolean;
  /**
   * What is being searched for the payment's receipt, until the search is
   * back: the mailbox picked - or none, every mailbox searched for the words
   * typed.
   */
  searching: { connectionIdentifier: string | null; query?: string } | null;
  /**
   * The last search of the payment's mailboxes that could not run - the
   * mailbox picked, or none, the words typed - and why, as the server said:
   * until another is asked for. Not "nothing found": nothing was asked.
   */
  searchFailure: {
    connectionIdentifier: string | null;
    query?: string;
    message: string;
  } | null;
  /**
   * The document found in a mailbox being looked at, by its identifier: open in
   * a dialog of what the search found, to import or not.
   */
  previewingEmailConnectionSearchResult: string | null;
  setPreviewingEmailConnectionSearchResult: (identifier: string | null) => void;
  /**
   * Given by the suggestions as they are drawn: the keyboard stepped *by* stops
   * through them, as the arrow keys step it there - from the panel's search.
   */
  registerStepSuggestions?: (step: (by: 1 | -1) => void) => void;
  /** The keyboard in the panel's search: a stop's key is pressed after Escape, which leaves it. */
  isSearchFocused?: boolean;
  /** The stops opened under themselves to what they were set beside - by → or their chevron, not by being picked. */
  expandedOptionKeys?: string[];
  setOptionExpanded?: (key: string, expanded: boolean) => void;
  /**
   * The list being picked from by hand - its transactions, documents or
   * accounts - open in a dialog: from a card with nothing suggested on it, a
   * card's way to all it searches, or a payment's "No document".
   */
  reconcilingManually: TriageResolutionTab | null;
  setReconcilingManually: (tab: TriageResolutionTab | null) => void;
  /**
   * A document found in a mailbox and imported, being read in its chat: drawn
   * where it was, as the draft it is read into - what the search said of it,
   * until that draft is in.
   */
  importingEmailConnectionSearchResult:
    | TriageOptionListItem["searchResult"]
    | null;
  setImportingEmailConnectionSearchResult: (
    importing: TriageOptionListItem["searchResult"] | null,
  ) => void;
  /** Whether the entry the payment would book is open under the suggestions. */
  isPreviewOpen: boolean;
  togglePreview: () => void;
  /**
   * The keyboard on a way to the payment's receipt - a mailbox to search, a
   * business to forward it to: nothing is picked to book or to look over.
   */
  isOnWayToDocument: boolean;
  journalEntryIsBalanced: boolean;
  /**
   * The business it is booked in besides its own - picked, said by its Move,
   * or booked so - that its Preview shows the entry in. Shown, not checked:
   * Book's balance is ``journalEntryIsBalanced``'s.
   */
  journalSettlement: TriageJournalSettlementContext | null;
  /**
   * Its payee's others that its Book would book differently, as its footer
   * works them out: how many (none while nothing is picked to book), whether
   * still being counted, and whether they can be booked - drawn as the
   * suggestions toolbar's Similar.
   */
  bookLikeOffer?: TriageBookLikeOffer | null;
  setBookLikeOffer?: (offer: TriageBookLikeOffer | null) => void;
  /**
   * Similar on - the chip in the queue's search, or L: the queue is the
   * payment's payee's others, ticked, and its suggestions the bulk list of
   * what to do to them all.
   */
  isSimilar?: boolean;
  setIsSimilar?: (isSimilar: boolean) => void;
  /** The queue's filter - its search and its queues - Similar's list narrowed by it too, as the bar the two share says. */
  queueFilter?: TransactionFilter | null;
  /** What Book does to them, told by the bulk list as its row is picked. */
  bulkBook?: TriageBulkBook | null;
  setBulkBook?: (book: TriageBulkBook | null) => void;
  /**
   * The match the bulk list's row picked - its chip in the panel's search:
   * the panel the match's side of it, the queue's place its payments, line
   * by line. None, the bulk list.
   */
  bulkMatch?: TriageBulkMatch | null;
  setBulkMatch?: (match: TriageBulkMatch | null) => void;
  /**
   * What the bulk list's row picked, by a click or Enter on it there and
   * nowhere else: a post (``post:``, ``account:``) or a move's stop
   * (``move:<business>:<treatment>``). It alone says what is staged on the
   * cards, ticked in the list and booked by Book - none, nothing is, whatever
   * else is picked for the payment.
   */
  bulkPick?: string | null;
  setBulkPick?: (pick: string | null) => void;
  /** How the suggestion picked settles in another business where no other way is said: the way its card recommends, as the server books it. */
  recommendedTreatment?: SettlementTreatmentEnum | null;
  /** The business of what each payment the match lines up is lined up with: payment → business. */
  linedUp?: ReadonlyMap<string, TriageLinedBusiness> | null;
  setLinedUp?: (
    linedUp: ReadonlyMap<string, TriageLinedBusiness> | null,
  ) => void;
  /** Each kind's own actions, for its button in the toolbar - none while its suggestions are not drawn. */
  groupMenus?: TriageGroupMenu[] | null;
  setGroupMenus?: (menus: TriageGroupMenu[] | null) => void;
  /** Where the line-up stands, for the toolbar to say - none, no match picked. */
  lineUpStatus?: TriageLineUpStatus | null;
  setLineUpStatus?: (status: TriageLineUpStatus | null) => void;
  /** How far past the payments' last row the line-up reaches (what is unplaced): the list scrolls as far. */
  lineUpRoom?: number;
  setLineUpRoom?: (room: number) => void;
  /** The list of the payee's others in the queue's place: what a match lines up beside each row of. */
  similarListElement?: HTMLElement | null;
  setSimilarListElement?: (element: HTMLElement | null) => void;
  registerCatalogChartedAccounts: (
    accounts: TriageChartedAccountCatalogItem[],
  ) => void;
  registerCatalogDocuments: (documents: DocumentFragment[]) => void;
  registerCatalogTransactions: (transactions: TransactionFragment[]) => void;
};

export type TriageActiveRowStaging = {
  triageIdentifier: string;
  selections: TriageSuggestionSelections;
  /** Its Move, drawn on its row: moved as the detail moves it. */
  movePayment: TriageDetailContextValue["movePayment"];
  /** The business it is being moved to, till its suggestions there are read. */
  movingPaymentTo: string | null;
  /** The AI at work on it, or a move of it asked. */
  isProcessing: boolean;
  /** Similar on: its payee's others moved with it from the bulk list - no Move of its own on its row. */
  isSimilar: boolean;
  /** How the suggestion picked settles in another business where no other way is said, as the server books it. */
  recommendedTreatment: SettlementTreatmentEnum | null;
  /** Its tags changed on its row: the ones it is booked with. */
  setTags: (tags: TriageSelectedTag[]) => void;
  triage: Pick<
    TriageDetail,
    | "identifier"
    | "amount"
    | "entity"
    | "belongsToEntity"
    | "settlementTreatment"
    | "isBooked"
    | "tags"
    | "suggestions"
  >;
};

export const TriageDetailContext =
  createContext<TriageDetailContextValue | null>(null);

export function useTriageDetailContext() {
  const context = useContext(TriageDetailContext);
  if (context == null) {
    throw new Error("useTriageDetailContext must be used within TriageDetail");
  }
  return context;
}

/**
 * Shared identity for the empty case so ``TriageView`` can bail out of storing a
 * new journal resolution — a fresh `[]` there re-renders the whole triage list.
 */
const EMPTY_RESOLVED_LIST: never[] = [];

export function resolveTriageOptions(
  triage: TriageDetail,
  selections: TriageSuggestionSelections,
  catalog: TriageOptionCatalog = {
    chartedAccountsByIdentifier: {},
    documentsByIdentifier: {},
    transactionsByIdentifier: {},
  },
): TriageResolvedOptions {
  const suggestions = (triage.suggestions ?? []) as TriageSuggestionStub[];
  const categorizeSuggestion =
    suggestions.find((suggestion) => suggestion.job === "CATEGORIZE") ?? null;
  const documentSuggestion =
    suggestions.find((suggestion) => suggestion.job === "MATCH_DOCUMENT") ??
    null;
  const duplicateSuggestion =
    suggestions.find((suggestion) => suggestion.job === "DUPLICATE") ?? null;

  const tagSuggestion =
    suggestions.find(
      (suggestion) => (suggestion.ruleSuggestionTagOptions?.length ?? 0) > 0,
    ) ?? null;

  const categoryOption = (() => {
    if (!selections.categoryIdentifier) {
      return null;
    }
    // One of the picked account's own, chosen on its row - an account found
    // for what was typed.
    const account = selections.chartedAccountIdentifier
      ? catalog.chartedAccountsByIdentifier[selections.chartedAccountIdentifier]
      : undefined;
    const category = account?.categories?.find(
      (one) => one.identifier === selections.categoryIdentifier,
    );
    return account && category
      ? ({
          id: category.id,
          identifier: category.identifier,
          category: {
            ...category,
            // The account it narrows.
            parent: {
              id: account.id,
              identifier: account.identifier,
              name: account.name,
            },
          },
        } as TriageCategoryOption)
      : null;
  })();

  const documentOptions = !selections.documentIdentifiers.length
    ? EMPTY_RESOLVED_LIST
    : selections.documentIdentifiers
        .map((identifier) => {
          const fromSuggestion =
            documentSuggestion?.ruleSuggestionDocumentOptions?.find(
              (option) => option.document?.identifier === identifier,
            ) ?? null;
          if (fromSuggestion) {
            return fromSuggestion;
          }
          const document = catalog.documentsByIdentifier[identifier];
          if (document) {
            return {
              id: document.id,
              identifier: document.identifier,
              // Picked by hand: what else pays it is not asked for.
              document: { ...document, paymentShares: [] },
            } as TriageDocumentOption;
          }
          const nodeId =
            selections.documentNodeIdsByIdentifier[identifier] ??
            identifierAndTypenameToId(identifier, "DocumentObject");
          return {
            id: nodeId,
            identifier,
            document: {
              __typename: "DocumentObject" as const,
              id: nodeId,
              identifier,
            },
          } as TriageDocumentOption;
        })
        .filter((option) => option != null);

  const documentOption = documentOptions[0] ?? null;

  const tagOptionIds = selections.tagOptionIds ?? [];

  const tagOption =
    tagSuggestion?.ruleSuggestionTagOptions?.find(
      (option) => option.id === tagOptionIds[0],
    ) ?? null;

  const tagOptions =
    tagSuggestion?.ruleSuggestionTagOptions?.filter((option) =>
      tagOptionIds.includes(option.id),
    ) ?? [];

  const chartedAccountOption = (() => {
    if (!selections.chartedAccountIdentifier) {
      return null;
    }

    const fromCategorizeSuggestion =
      categorizeSuggestion?.ruleSuggestionChartedAccountOption?.chartedAccount
        ?.identifier === selections.chartedAccountIdentifier
        ? categorizeSuggestion.ruleSuggestionChartedAccountOption
        : null;
    if (fromCategorizeSuggestion) {
      return fromCategorizeSuggestion;
    }

    const account =
      catalog.chartedAccountsByIdentifier[selections.chartedAccountIdentifier];
    if (!account) {
      return null;
    }
    return {
      id: account.id,
      identifier: account.identifier,
      chartedAccount: {
        ...account,
        path: account.path ?? "",
        fullPath: account.fullPath ?? [],
      },
    };
  })();

  const duplicateOption =
    duplicateSuggestion?.ruleSuggestionTransactionOptions?.find(
      (option) => option.id === selections.duplicateOptionId,
    ) ?? null;

  const selectedTransactionIdentifiers =
    selections.transactionIdentifiers ?? [];
  const selectedTransactions = !selectedTransactionIdentifiers.length
    ? EMPTY_RESOLVED_LIST
    : selectedTransactionIdentifiers
        .map((identifier) => catalog.transactionsByIdentifier[identifier])
        .filter(
          (transaction): transaction is TransactionFragment =>
            transaction != null,
        );

  const linesSuggestion = (() => {
    if (!selections.linesSuggestionIdentifier) {
      return null;
    }
    return (
      suggestions.find(
        (suggestion) =>
          suggestion.identifier === selections.linesSuggestionIdentifier &&
          (suggestion.lines?.length ?? 0) > 0,
      ) ?? null
    );
  })();

  return {
    categoryOption,
    documentOption,
    documentOptions,
    tagOption,
    tagOptions,
    chartedAccountOption,
    duplicateOption,
    categorizeSuggestion,
    documentSuggestion,
    duplicateSuggestion,
    linesSuggestion,
    selectedTransactions,
  };
}

export function resolveActivePreview(
  triage: TriageDetail,
  activeOptionKey: string | null,
): TriageActivePreview {
  const empty: TriageActivePreview = {
    kind: null,
    categoryOption: null,
    tagOption: null,
    documentOption: null,
    chartedAccountOption: null,
    duplicateOption: null,
    categorizeSuggestion: null,
    documentSuggestion: null,
    duplicateSuggestion: null,
  };

  if (!activeOptionKey) {
    return empty;
  }

  const suggestions = (triage.suggestions ?? []) as TriageSuggestionStub[];
  const categorizeSuggestion =
    suggestions.find((suggestion) => suggestion.job === "CATEGORIZE") ?? null;
  const documentSuggestion =
    suggestions.find((suggestion) => suggestion.job === "MATCH_DOCUMENT") ??
    null;
  const duplicateSuggestion =
    suggestions.find((suggestion) => suggestion.job === "DUPLICATE") ?? null;
  const tagSuggestion =
    suggestions.find(
      (suggestion) => (suggestion.ruleSuggestionTagOptions?.length ?? 0) > 0,
    ) ?? null;

  const colonIndex = activeOptionKey.indexOf(":");
  if (colonIndex < 0) {
    return empty;
  }

  const kind = activeOptionKey.slice(0, colonIndex);
  const optionId = activeOptionKey.slice(colonIndex + 1);

  if (kind === "account") {
    const chartedAccountOption =
      categorizeSuggestion?.ruleSuggestionChartedAccountOption?.chartedAccount
        ?.identifier === optionId
        ? categorizeSuggestion.ruleSuggestionChartedAccountOption
        : null;
    return {
      ...empty,
      kind: "account",
      chartedAccountOption,
      categorizeSuggestion,
    };
  }

  if (kind === "category") {
    // A category is picked on its account's row: no suggestion offers one of its own to look over.
    return { ...empty, kind: "category", categorizeSuggestion };
  }

  if (kind === "tag") {
    const tagOption =
      tagSuggestion?.ruleSuggestionTagOptions?.find(
        (option) => option.id === optionId,
      ) ?? null;
    return { ...empty, kind: "tag", tagOption };
  }

  if (kind === "document") {
    const documentOption =
      documentSuggestion?.ruleSuggestionDocumentOptions?.find(
        (option) =>
          option.document?.identifier === optionId || option.id === optionId,
      ) ?? null;
    return {
      ...empty,
      kind: "document",
      documentOption,
      documentSuggestion,
    };
  }

  if (kind === "duplicate") {
    const duplicateOption =
      duplicateSuggestion?.ruleSuggestionTransactionOptions?.find(
        (option) => option.id === optionId,
      ) ?? null;
    return {
      ...empty,
      kind: "duplicate",
      duplicateOption,
      duplicateSuggestion,
    };
  }

  return empty;
}

export function resolveJournalPreviewOffset(
  triage: TriageDetail,
  activePreview: TriageActivePreview,
  resolved: TriageResolvedOptions,
): string | null {
  if (
    activePreview.kind === "account" &&
    activePreview.chartedAccountOption?.chartedAccount?.name
  ) {
    return activePreview.chartedAccountOption.chartedAccount.name;
  }

  if (resolved.chartedAccountOption?.chartedAccount?.name) {
    return resolved.chartedAccountOption.chartedAccount.name;
  }

  if (
    activePreview.kind === "category" &&
    activePreview.categoryOption?.category?.name
  ) {
    return activePreview.categoryOption.category.name;
  }

  if (resolved.categoryOption?.category?.name) {
    return resolved.categoryOption.category.name;
  }

  const categoryAccount = (
    triage.categoryObj as {
      chartedAccount?: { name?: string | null } | null;
      name?: string | null;
    } | null
  )?.chartedAccount?.name;
  if (categoryAccount) {
    return categoryAccount;
  }

  return triage.categoryObj?.name ?? null;
}

export function defaultTriageSelections(): TriageSuggestionSelections {
  return {
    categoryIdentifier: null,
    chartedAccountIdentifier: null,
    documentIdentifiers: [],
    documentNodeIdsByIdentifier: {},
    transactionIdentifiers: [],
    tagOptionIds: [],
    selectedTags: [],
    settlementTags: [],
    chartedAccountOptionId: null,
    duplicateOptionId: null,
    settlementEntityIdentifier: null,
    linesSuggestionIdentifier: null,
    suggestionCategoryIdentifier: null,
    suggestionNewCategoryName: null,
    newCategoryName: null,
    draftDocumentIdentifier: null,
    settlementTreatment: null,
    matchesKeptBySuggestion: {},
    partsBySuggestion: {},
  };
}

export function defaultActiveOptionKey(
  triage: TriageDetail,
  selections: TriageSuggestionSelections,
): string | null {
  const tagOptionIds = selections.tagOptionIds ?? [];
  const documentIdentifiers = selections.documentIdentifiers ?? [];
  if (selections.linesSuggestionIdentifier) {
    return `lines:${selections.linesSuggestionIdentifier}`;
  }
  if (selections.chartedAccountIdentifier) {
    return `account:${selections.chartedAccountIdentifier}`;
  }
  if (documentIdentifiers[0]) {
    return `document:${documentIdentifiers[0]}`;
  }
  if (selections.transactionIdentifiers?.[0]) {
    return `transaction:${selections.transactionIdentifiers[0]}`;
  }
  if (selections.settlementEntityIdentifier) {
    return `entity:${selections.settlementEntityIdentifier}`;
  }
  if (selections.duplicateOptionId) {
    return `duplicate:${selections.duplicateOptionId}`;
  }
  if (tagOptionIds[0]) {
    return `tag:${tagOptionIds[0]}`;
  }

  const suggestions = (triage.suggestions ?? []) as TriageSuggestionStub[];
  const firstLinesSuggestion = suggestions.find(
    (suggestion) => (suggestion.lines?.length ?? 0) > 0,
  );
  if (firstLinesSuggestion?.identifier) {
    return `lines:${firstLinesSuggestion.identifier}`;
  }

  const categorizeSuggestion = suggestions.find(
    (suggestion) => suggestion.job === "CATEGORIZE",
  );
  const firstCategoryAccount =
    categorizeSuggestion?.ruleSuggestionChartedAccountOption?.chartedAccount
      ?.identifier;
  if (firstCategoryAccount) {
    return `account:${firstCategoryAccount}`;
  }

  const firstDocument = suggestions.find(
    (suggestion) => suggestion.job === "MATCH_DOCUMENT",
  )?.ruleSuggestionDocumentOptions?.[0];
  if (firstDocument?.document?.identifier) {
    return `document:${firstDocument.document.identifier}`;
  }

  const firstDuplicate = suggestions.find(
    (suggestion) => suggestion.job === "DUPLICATE",
  )?.ruleSuggestionTransactionOptions?.[0];
  if (firstDuplicate) {
    return `duplicate:${firstDuplicate.id}`;
  }

  return null;
}

export function linesSuggestionDocumentSelections(
  suggestion: TriageSuggestionStub,
): Pick<
  TriageSuggestionSelections,
  "documentIdentifiers" | "documentNodeIdsByIdentifier"
> {
  const documentIdentifiers: string[] = [];
  const documentNodeIdsByIdentifier: Record<string, string> = {};
  for (const option of suggestion.ruleSuggestionDocumentOptions ?? []) {
    const document = option.document;
    if (!document?.identifier) {
      continue;
    }
    documentIdentifiers.push(document.identifier);
    if (document.id) {
      documentNodeIdsByIdentifier[document.identifier] = document.id;
    }
  }
  return { documentIdentifiers, documentNodeIdsByIdentifier };
}

export function toggleLinesSuggestion(
  suggestion: TriageSuggestionStub,
  currentLinesSuggestionIdentifier: string | null,
  setSelection: TriageDetailContextValue["setSelection"],
) {
  const optionId = suggestion.identifier;
  const deselecting = currentLinesSuggestionIdentifier === optionId;
  setSelection("linesSuggestionIdentifier", deselecting ? null : optionId);
  // Picked, it is what it is booked as: an account picked by hand before it
  // lets go, as picking one lets a suggestion go (``commitActiveOption``).
  if (!deselecting) {
    setSelection("chartedAccountIdentifier", null);
    setSelection("categoryIdentifier", null);
    setSelection("newCategoryName", null);
  }
  setSelection("suggestionCategoryIdentifier", null);
  setSelection("suggestionNewCategoryName", null);
  setSelection("draftDocumentIdentifier", null);
  if (deselecting) {
    setSelection("documentIdentifiers", []);
    setSelection("documentNodeIdsByIdentifier", {});
    return;
  }
  const { documentIdentifiers, documentNodeIdsByIdentifier } =
    linesSuggestionDocumentSelections(suggestion);
  setSelection("documentIdentifiers", documentIdentifiers);
  setSelection("documentNodeIdsByIdentifier", documentNodeIdsByIdentifier);
}

export function commitActiveOption(
  activeOptionKey: string | null,
  setSelection: TriageDetailContextValue["setSelection"],
) {
  if (!activeOptionKey) {
    return;
  }

  const colonIndex = activeOptionKey.indexOf(":");
  if (colonIndex < 0) {
    return;
  }

  const kind = activeOptionKey.slice(0, colonIndex);
  const optionId = activeOptionKey.slice(colonIndex + 1);
  // Picking anything else lets a draft go.
  if (kind !== "draft") {
    setSelection("draftDocumentIdentifier", null);
  }

  if (kind === "account") {
    setSelection("linesSuggestionIdentifier", null);
    setSelection("chartedAccountIdentifier", (current) =>
      current === optionId ? null : optionId,
    );
    setSelection("categoryIdentifier", null);
    setSelection("newCategoryName", null);
  } else if (kind === "category") {
    setSelection("linesSuggestionIdentifier", null);
    setSelection("categoryIdentifier", (current) =>
      current === optionId ? null : optionId,
    );
  } else if (kind === "entity") {
    setSelection("linesSuggestionIdentifier", null);
    setSelection("settlementEntityIdentifier", (current) =>
      current === optionId ? null : optionId,
    );
  } else if (kind === "duplicate") {
    setSelection("linesSuggestionIdentifier", null);
    setSelection("duplicateOptionId", (current) =>
      current === optionId ? null : optionId,
    );
  } else if (kind === "lines") {
    setSelection("linesSuggestionIdentifier", (current) =>
      current === optionId ? null : optionId,
    );
  } else if (kind === "draft") {
    // A draft in review, picked in place of whatever was: booked, it is
    // confirmed and booked at once.
    setSelection("linesSuggestionIdentifier", null);
    setSelection("documentIdentifiers", []);
    setSelection("documentNodeIdsByIdentifier", {});
    setSelection("draftDocumentIdentifier", (current) =>
      current === optionId ? null : optionId,
    );
  }
}
